"use client";

import { updateAdminCourseApiV1AdminCoursesCourseIdPatch } from "@/src/api/sdk.gen";
import { useState } from "react";
import { Eye, Pencil, Save } from "lucide-react";
import { PROMPT_LABELS } from "@/lib/admin/course-details";
import type { CourseDetails, PromptKey } from "@/lib/admin/course-details";

type PromptsTabProps = {
    course: CourseDetails;
    onPromptSaved: (key: PromptKey, value: string | null) => void;
};

export default function PromptsTab({ course, onPromptSaved }: PromptsTabProps) {
    const [promptEdits, setPromptEdits] = useState<Partial<Record<PromptKey, string>>>({});
    const [savingPrompt, setSavingPrompt] = useState<PromptKey | null>(null);
    const [promptModeByKey, setPromptModeByKey] = useState<Partial<Record<PromptKey, "read" | "edit">>>({});
    const [activePromptTab, setActivePromptTab] = useState<PromptKey>("generation_prompt");

    const getPromptValue = (key: PromptKey): string => {
        if (promptEdits[key] !== undefined) return promptEdits[key]!;
        return (course[key] as string | null | undefined) ?? "";
    };

    const handleSavePrompt = async (key: PromptKey) => {
        const value = getPromptValue(key);
        setSavingPrompt(key);
        try {
            const { data, error } = await updateAdminCourseApiV1AdminCoursesCourseIdPatch({
                path: { course_id: course.id },
                body: { [key]: value || null },
            });
            if (!error && data) {
                const updated = data as Partial<Record<PromptKey, string | null>>;
                onPromptSaved(key, updated[key] ?? null);
                setPromptEdits((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            } else {
                const err = (error || {}) as { error?: string };
                alert(err.error || "Failed to save prompt");
            }
        } catch (e) {
            console.error("Failed to save prompt:", e);
            alert("An error occurred while saving.");
        } finally {
            setSavingPrompt(null);
        }
    };

    const key = activePromptTab;
    const value = getPromptValue(key);
    const isDirty = value !== ((course[key] as string | null | undefined) ?? "");
    const isSaving = savingPrompt === key;
    const mode = promptModeByKey[key] ?? "read";
    const lineCount = value ? value.split("\n").length : 0;
    const charCount = value.length;

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/60">
                <h2 className="text-lg font-bold text-slate-900">Course prompts</h2>
                <p className="text-sm text-slate-500">
                    Prompts used for lesson generation, quiz, placement quiz, and remedial flows.
                </p>
            </div>
            <div className="border-b border-slate-200 bg-white px-4 py-2">
                <div className="flex flex-wrap gap-2">
                    {(Object.keys(PROMPT_LABELS) as PromptKey[]).map((promptKey) => (
                        <button
                            key={promptKey}
                            type="button"
                            onClick={() => setActivePromptTab(promptKey)}
                            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                activePromptTab === promptKey
                                    ? "border-amber-300 bg-amber-100 text-amber-900"
                                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                            }`}
                        >
                            {PROMPT_LABELS[promptKey]}
                        </button>
                    ))}
                </div>
            </div>
            <div className="px-6 py-5 bg-gradient-to-b from-white to-slate-50/50">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <label className="block text-sm font-semibold text-slate-800">
                            {PROMPT_LABELS[key]}
                        </label>
                        <p className="mt-1 text-xs text-slate-500">
                            {lineCount} lines - {charCount} characters
                        </p>
                    </div>
                    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-1">
                        <button
                            type="button"
                            onClick={() => setPromptModeByKey((prev) => ({ ...prev, [key]: "read" }))}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                                mode === "read"
                                    ? "bg-slate-100 text-slate-900"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <Eye className="h-3.5 w-3.5" />
                            Read
                        </button>
                        <button
                            type="button"
                            onClick={() => setPromptModeByKey((prev) => ({ ...prev, [key]: "edit" }))}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                                mode === "edit"
                                    ? "bg-amber-100 text-amber-900"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                        </button>
                    </div>
                </div>
                {mode === "read" ? (
                    <div className="max-h-[420px] overflow-y-auto rounded-xl border border-slate-200 bg-white px-4 py-3">
                        {value ? (
                            <pre className="whitespace-pre-wrap break-words text-sm leading-7 text-slate-800 font-mono">
                                {value}
                            </pre>
                        ) : (
                            <p className="text-sm italic text-slate-400">No prompt set yet.</p>
                        )}
                    </div>
                ) : (
                    <textarea
                        value={value}
                        onChange={(e) => setPromptEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder={`Add ${PROMPT_LABELS[key].toLowerCase()}...`}
                        rows={14}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-7 text-slate-900 font-mono placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                )}
                <div className="mt-3 flex justify-end">
                    <button
                        type="button"
                        onClick={() => handleSavePrompt(key)}
                        disabled={!isDirty || isSaving}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    >
                        <Save className="h-4 w-4" />
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
